import { memo } from "react";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { type StreamMessage } from "../../hooks/useStreamingText";
import { Text } from "../Text";

import { ActivityLineRow, resolveActionLine } from "./ActivityLineRow";
import type { GroupedEntry } from "./groupChatEntries";
import { KickoffRow, resolveKickoff } from "./KickoffRow";
import { ResearchCitations } from "./ResearchCitations";
import { MessageRow } from "./MessageRow";
import { ToolGroupRow } from "./ToolGroupRow";
import { DateBadge, DateMarker } from "./transcriptStyles";
import { UserActionGroupRow } from "./UserActionGroupRow";
import { WorkflowChangeReview } from "./WorkflowChangeReview";
import type { ChatProps } from "./chatTypes";
import { useTranscriptExpansion } from "./TranscriptContext";

function formatDateLabel(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const entryDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  if (entryDay.getTime() === today.getTime()) return "Today";
  if (entryDay.getTime() === yesterday.getTime()) return "Yesterday";

  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
    ...(date.getFullYear() !== now.getFullYear() && { year: "numeric" }),
  };
  return date.toLocaleDateString("en-US", options);
}

function getGroupTimestamp(grouped: GroupedEntry): number {
  if (grouped.type === "single") return grouped.entry.createdAt;
  if (grouped.type === "userActionGroup")
    return grouped.entries[0]?.createdAt ?? 0;
  if (grouped.entries[0]) return grouped.entries[0].createdAt;
  return 0;
}

function getDateKey(timestamp: number): string {
  const d = new Date(timestamp);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

type TranscriptProps = {
  reviewVersion?: ChatProps["reviewVersion"];
  entries: readonly ChatEntryWithSubTables[];
  groupedEntries: GroupedEntry[];
  streamingEntries: ReadonlyMap<string, StreamMessage>;
};

export const Transcript = memo(function Transcript({
  reviewVersion,
  entries,
  groupedEntries,
  streamingEntries,
}: TranscriptProps) {
  const { setExpandedToolGroups } = useTranscriptExpansion();
  const showDiscussion = (entryId: string) => {
    const group = groupedEntries.find(
      (grouped) =>
        grouped.type === "toolGroup" &&
        grouped.entries.some((entry) => entry.id === entryId)
    );
    if (group?.type === "toolGroup") {
      setExpandedToolGroups((previous) => new Set(previous).add(group.groupId));
    }
  };
  const rowTimestamps: number[] = [];
  for (const grouped of groupedEntries) {
    rowTimestamps.push(
      Math.max(getGroupTimestamp(grouped), rowTimestamps.at(-1) ?? 0)
    );
  }
  const rows = groupedEntries.map((grouped, index) => {
    const nextGrouped = groupedEntries[index + 1];
    const prevGrouped = groupedEntries[index - 1];

    const currentTimestamp = rowTimestamps[index]!;
    const prevTimestamp = rowTimestamps[index - 1] ?? null;
    const showDateMarker =
      currentTimestamp > 0 &&
      (prevTimestamp === null ||
        getDateKey(currentTimestamp) !== getDateKey(prevTimestamp));
    const dateMarker = showDateMarker ? (
      <DateMarker key={`date-${getDateKey(currentTimestamp)}`}>
        <DateBadge>
          <Text variant="text-small" color="var(--text-secondary)">
            {formatDateLabel(currentTimestamp)}
          </Text>
        </DateBadge>
      </DateMarker>
    ) : null;

    if (grouped.type === "userActionGroup") {
      return (
        <UserActionGroupRow
          key={grouped.groupId}
          grouped={grouped}
          dateMarker={dateMarker}
          hasPrev={!!prevGrouped}
          hasNext={!!nextGrouped}
        />
      );
    }

    if (grouped.type === "toolGroup") {
      return (
        <ToolGroupRow
          key={`activity-${grouped.groupId}`}
          grouped={grouped}
          prevGrouped={prevGrouped}
          nextGrouped={nextGrouped}
          dateMarker={dateMarker}
          streamingEntries={streamingEntries}
        />
      );
    }

    const entry = grouped.entry;

    const kickoff = resolveKickoff(entry);
    if (kickoff) {
      return (
        <KickoffRow
          key={entry.id}
          kickoff={kickoff}
          dateMarker={dateMarker}
          hasPrev={!!prevGrouped}
          hasNext={!!nextGrouped}
        />
      );
    }

    const actionLine = resolveActionLine(entry);
    if (actionLine) {
      return (
        <ActivityLineRow
          key={entry.id}
          actionLine={actionLine}
          dateMarker={dateMarker}
          hasPrev={!!prevGrouped}
          hasNext={!!nextGrouped}
        />
      );
    }

    return (
      <MessageRow
        key={entry.id}
        entry={entry}
        nextGrouped={nextGrouped}
        interrupted={grouped.interrupted ?? false}
        dateMarker={dateMarker}
        streamingEntries={streamingEntries}
      />
    );
  });
  const hasCitations = entries.some((entry) =>
    (
      streamingEntries.get(entry.id)?.text ??
      entry.assistantMessage?.text ??
      ""
    ).includes("(#source-")
  );
  return (
    <ResearchCitations entries={entries} enabled={hasCitations}>
      <div data-conversation-transcript style={{ display: "contents" }}>
        {rows}
        {reviewVersion && (
          <WorkflowChangeReview
            key={reviewVersion.commitId}
            version={reviewVersion}
            entries={entries}
            onShowDiscussion={showDiscussion}
          />
        )}
      </div>
    </ResearchCitations>
  );
});
